import React, { useMemo } from 'react';
import Modal from '../../BaseComponents/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Select from '../../BaseComponents/Select';
import Button from '../../BaseComponents/Button';
import countryList from 'react-select-country-list';
import './MemberInfoCard.scss';
import { Formik } from 'formik';
import TextInput from '../../BaseComponents/TextInput';
import { assignAddress } from '../../../api/members';
import { SetMemberAddressRequestPydantic } from '../../../types/apiGeneratedTypes';
import { updateMemberAddress } from '../../../store/memberSlice';
import { CustomInput } from '../../location/CustomInput';

type ChangeAddressModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export interface FormValues {
  country: { key: any; value: any };
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
}

const ChangeAddressModal: React.FC<ChangeAddressModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);

  const { memberData } = useSelector((state: RootState) => state.member);

  const options = useMemo(
    () =>
      countryList()
        .getData()
        .map((country) => ({ key: country.label, value: country.label })),
    []
  );

  const initialValues: FormValues = {
    country: {
      key: memberData?.country ?? '',
      value: memberData?.country ?? '',
    },
    address1: memberData?.address_line_1 ?? '',
    address2: memberData?.address_line_2 ?? '',
    city: memberData?.city ?? '',
    postalCode: memberData?.post_code ?? '',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={'400px'}>
      <div className='assign-doctor-modal'>
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, { resetForm }) => {
            try {
              setLoading(true);
              const requestObject: SetMemberAddressRequestPydantic = {
                address_line_1: values.address1,
                address_line_2: values.address2,
                city: values.city,
                post_code: values.postalCode,
                country: values.country.key,
                member_id: memberData?.user_id!,
              };
              const resp = await assignAddress(requestObject);
              if (resp) {
                dispatch(updateMemberAddress(requestObject));
              }
              onClose();
              resetForm();
            } catch (error) {
              setError(true);
            } finally {
              setLoading(false);
            }
          }}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          {({ handleSubmit, setFieldValue, values }) => (
            <form onSubmit={handleSubmit}>
              <div className='select-address-container'>
                <div className='address-search-wrapper input-wrapper'>
                  <div className='text-input-title '>Address</div>
                  <CustomInput
                    value={values.address1}
                    onChange={(value: string) => {
                      // Parse the address components from the Loqate response
                      const line1 = (document.getElementsByClassName('line-1')[0] as HTMLInputElement)?.value;
                      const line2 = (document.getElementsByClassName('line-2')[0] as HTMLInputElement)?.value;
                      const city = (document.getElementsByClassName('city')[0] as HTMLInputElement)?.value;
                      const postalCode = (document.getElementsByClassName('postcode')[0] as HTMLInputElement)?.value;
                      const country = (document.getElementsByClassName('country')[0] as HTMLInputElement)?.value;

                      // Update all form fields
                      setFieldValue('address1', line1);
                      setFieldValue('address2', line2);
                      setFieldValue('city', city);
                      setFieldValue('postalCode', postalCode);
                      setFieldValue('country', { key: country, value: country });
                    }}
                  />
                </div>
                <div className='text-input-title '>Address Line 2</div>
                <TextInput
                  name='address2'
                  placeholder='Enter Address Line 2'
                  isError={false}
                  onChange={(e) => setFieldValue('address2', e.target.value)}
                  value={values.address2}
                />
                <div className='text-input-title '>City *</div>
                <TextInput
                  name='city'
                  placeholder='Enter City'
                  isError={false}
                  onChange={(e) => setFieldValue('city', e.target.value)}
                  value={values.city}
                />
                <div className='text-input-title '>Post Code *</div>
                <TextInput
                  name='postalCode'
                  placeholder='Enter Post Code'
                  isError={false}
                  onChange={(e) => setFieldValue('postalCode', e.target.value)}
                  value={values.postalCode}
                />
                <div className='text-input-title '>Country *</div>
                <Select
                  name='country'
                  placeholder='Select country'
                  options={options}
                  selectedOption={values.country}
                  onChange={(option) => setFieldValue('country', option)}
                  isError={false}
                />
              </div>

              <div className='submit-btn-wrapper'>
                <Button
                  text='Assign'
                  type='submit'
                  isLoading={loading}
                  isDisabled={
                    !values.address1 ||
                    !values.city ||
                    !values.postalCode ||
                    !values.country.key
                  }
                />
                <Button
                  isLoading={loading}
                  text='Cancel'
                  onClick={onClose}
                  backgroundColor={'#ffffff'}
                  color={'#2D334E'}
                />
              </div>
              {error && (
                <div className='error-message'>
                  Something went wrong. Please try again later
                </div>
              )}
            </form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};
export default ChangeAddressModal;
